import { useState, useEffect, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calculator, ArrowLeft, ChevronDown, ChevronUp, Shield, Users, Clock, Building2, Car, Radio, Printer, DoorOpen, AlertTriangle, Download, FileText, Plus, Trash2, Settings, Monitor, Phone } from "lucide-react";
import ProjectSupportDialog from "@/components/ProjectSupportDialog";
import { calculateSundryTotal } from "@/components/SundryItemsManager";
import QuoteTargetSelector from "@/components/QuoteTargetSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

// Module definitions - Access Control is now a module
const ALL_MODULES = [
  { id: 'access_control', name: 'Access Control', icon: DoorOpen, description: 'Door access control with readers and controllers', isMain: true },
];

// Advanced functionality modules (shown in modal within Access Control)
const ADVANCED_MODULES = [
  { id: 'time_attendance', name: 'Time and Attendance', icon: Clock, description: 'Employee time tracking and reporting' },
  { id: 'visitor_management', name: 'Visitor Management', icon: Users, description: 'Software based visitor management system' },
  { id: 'elevator', name: 'Elevator Control', icon: Building2, description: 'Floor access control for elevators' },
  { id: 'parking', name: 'Parking Management', icon: Car, description: 'Vehicle access and parking control' },
  { id: 'guard_patrol', name: 'Guard Patrol', icon: Shield, description: 'Security patrol tracking and checkpoints' },
];

// Credential types for Access Control
const CREDENTIAL_TYPES = [
  { id: 'face', name: 'Face', description: 'Facial recognition' },
  { id: 'palm', name: 'Palm', description: 'Palm vein recognition' },
  { id: 'mifare_rfid', name: 'Mifare RFID', description: 'Contactless card' },
  { id: 'pin_mifare', name: 'Pin and Mifare RFID', description: 'PIN + card combination' },
  { id: 'qr_code', name: 'QR Code', description: 'QR code scanning' },
  { id: 'fingerprint', name: 'Fingerprint', description: 'Fingerprint biometric' },
  { id: 'bluetooth', name: 'Bluetooth', description: 'Mobile credential' },
];

const BioCVSecurity = ({ onBack, backLabel = "Back to Access Control" }) => {
  const [projectName, setProjectName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [installerOnlyCapture, setInstallerOnlyCapture] = useState(false);
  
  // Admin licenses (now in Access Control)
  const [adminLicenses, setAdminLicenses] = useState("");
  
  // Selected modules
  const [selectedModules, setSelectedModules] = useState([]);
  
  // Access Control module settings
  const [accessDoors, setAccessDoors] = useState("");
  const [needsReadInOut, setNeedsReadInOut] = useState("");
  const [readInOutDoors, setReadInOutDoors] = useState("");
  const [selectedCredentials, setSelectedCredentials] = useState([]);
  const [externalDoors, setExternalDoors] = useState("");  // For Face/Palm external readers
  const [needsCardPrinter, setNeedsCardPrinter] = useState("");
  const [printerCount, setPrinterCount] = useState("");
  const [needsEmergencyEvac, setNeedsEmergencyEvac] = useState("");
  const [evacExternalDoors, setEvacExternalDoors] = useState("");  // External doors for emergency evacuation
  const [qrMobileUsers, setQrMobileUsers] = useState("");
  
  // Bluetooth credential settings
  const [bluetoothLicenseType, setBluetoothLicenseType] = useState("");
  const [bluetoothUserCount, setBluetoothUserCount] = useState("");
  const [showBluetoothDialog, setShowBluetoothDialog] = useState(false);
  
  // Advanced Functionality Modal
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [selectedAdvancedModules, setSelectedAdvancedModules] = useState([]);
  const [needsAdvancedFunctionality, setNeedsAdvancedFunctionality] = useState(""); // "", "yes", "no"
  
  // Time & Attendance module settings (now part of Access Control advanced functionality)
  const [taAttendancePoints, setTaAttendancePoints] = useState("");
  const [taUserCount, setTaUserCount] = useState("");
  const [taDeviceType, setTaDeviceType] = useState(""); // "access_control" or "dedicated"
  const [taSelectedDevices, setTaSelectedDevices] = useState([]); // Array of {type, quantity}
  const [showTaContactDialog, setShowTaContactDialog] = useState(false);
  
  // Visitor Management module settings
  const [vmMonthlyVisitors, setVmMonthlyVisitors] = useState("");
  const [vmRegistrationSites, setVmRegistrationSites] = useState("");
  const [vmDedicatedTerminal, setVmDedicatedTerminal] = useState("");
  const [vmUnmannedReception, setVmUnmannedReception] = useState("");
  const [showVmContactDialog, setShowVmContactDialog] = useState(false);
  
  // Quote target audience (for ZKTeco staff only)
  const [quoteTarget, setQuoteTarget] = useState(""); // "distributor" or "installer"
  
  // UI state
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({
    modules: true
  });
  
  // Project Support
  const [showProjectSupport, setShowProjectSupport] = useState(false);
  const [supportDeclined, setSupportDeclined] = useState(false);
  
  // Sundry Items
  const [sundryItems, setSundryItems] = useState([]);
  const [showSundryDialog, setShowSundryDialog] = useState(false);
  const [editingSundryIndex, setEditingSundryIndex] = useState(null);
  const [sundryForm, setSundryForm] = useState({
    sku: '',
    description: '',
    quantity: 1,
    unit_price: 0
  });
  
  // Quote box ref for download
  const quoteBoxRef = useRef(null);

  // Sundry Items Functions
  const handleAddSundryItem = () => {
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
    setShowSundryDialog(true);
  };

  const handleEditSundryItem = (index) => {
    setSundryForm(sundryItems[index]);
    setEditingSundryIndex(index);
    setShowSundryDialog(true);
  };

  const handleSaveSundryItem = () => {
    if (!sundryForm.description || sundryForm.quantity <= 0) {
      setError('Please enter a description and valid quantity');
      return;
    }
    
    const newItem = {
      ...sundryForm,
      quantity: parseFloat(sundryForm.quantity) || 1,
      unit_price: parseFloat(sundryForm.unit_price) || 0,
      line_total: (parseFloat(sundryForm.quantity) || 1) * (parseFloat(sundryForm.unit_price) || 0)
    };
    
    if (editingSundryIndex !== null) {
      const updated = [...sundryItems];
      updated[editingSundryIndex] = newItem;
      setSundryItems(updated);
    } else {
      setSundryItems([...sundryItems, newItem]);
    }
    
    setShowSundryDialog(false);
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
  };

  const handleDeleteSundryItem = (index) => {
    if (window.confirm('Remove this sundry item?')) {
      setSundryItems(sundryItems.filter((_, i) => i !== index));
    }
  };

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        console.log('BioCVSecurity - User role:', userData.role);
        setUserRole(userData.role || '');
      } catch (e) {
        setUserRole('');
      }
    }
  }, []);

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCredentialSelect = (credentialId) => {
    // Single selection - replace array with just this credential
    setSelectedCredentials([credentialId]);
    
    // If Bluetooth is selected, show the Bluetooth configuration dialog
    if (credentialId === 'bluetooth') {
      setShowBluetoothDialog(true);
    } else {
      // Reset Bluetooth settings if another credential is selected
      setBluetoothLicenseType("");
      setBluetoothUserCount("");
    }
  };

  const handleModuleToggle = (moduleId) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        // Remove module and reset its settings if it's access_control
        if (moduleId === 'access_control') {
          setAccessDoors("");
          setNeedsReadInOut("");
          setReadInOutDoors("");
          setSelectedCredentials([]);
          setNeedsCardPrinter("");
          setPrinterCount("");
          setNeedsEmergencyEvac("");
          setQrMobileUsers("");
          setAdminLicenses("");
          setBluetoothLicenseType("");
          setBluetoothUserCount("");
          // Reset advanced modules when Access Control is removed
          setNeedsAdvancedFunctionality("");
          setSelectedAdvancedModules([]);
          setTaAttendancePoints("");
          setTaUserCount("");
          setTaDeviceType("");
          setTaSelectedDevices([]);
        }
        return prev.filter(m => m !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };
  
  // Handle advanced functionality Yes/No selection
  const handleAdvancedFunctionalityChange = (value) => {
    setNeedsAdvancedFunctionality(value);
    if (value === 'no') {
      // Clear all selected advanced modules when No is selected
      setSelectedAdvancedModules([]);
      setTaAttendancePoints("");
      setTaUserCount("");
      setTaDeviceType("");
      setTaSelectedDevices([]);
    }
  };
  
  // Handle advanced module toggle
  const handleAdvancedModuleToggle = (moduleId) => {
    if (needsAdvancedFunctionality !== 'yes') return; // Don't allow toggling unless Yes is selected
    
    setSelectedAdvancedModules(prev => {
      if (prev.includes(moduleId)) {
        // Reset module-specific settings when unchecked
        if (moduleId === 'time_attendance') {
          setTaAttendancePoints("");
          setTaUserCount("");
          setTaDeviceType("");
          setTaSelectedDevices([]);
        }
        if (moduleId === 'visitor_management') {
          setVmMonthlyVisitors("");
          setVmRegistrationSites("");
          setVmDedicatedTerminal("");
          setVmUnmannedReception("");
        }
        return prev.filter(m => m !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const accessControlSelected = selectedModules.includes('access_control');
  const timeAttendanceSelected = selectedAdvancedModules.includes('time_attendance');
  const visitorManagementSelected = selectedAdvancedModules.includes('visitor_management');
  const doorsNum = parseInt(accessDoors) || 0;
  const hasQrCode = selectedCredentials.includes('qr_code');
  const hasBluetooth = selectedCredentials.includes('bluetooth');
  const hasFaceOrPalm = selectedCredentials.includes('face') || selectedCredentials.includes('palm');
  
  // Time & Attendance device options
  const TA_DEVICES = [
    { id: 'rfid', sku: 'TA-ICLOCK-S880ZMM220-12', name: 'RFID Terminal', description: 'S880 RFID Time & Attendance', datasheet: 'https://customer-assets.emergentagent.com/job_biosecurity-hub/artifacts/8b9b6gpo_S880.pdf' },
    { id: 'finger', sku: 'TA-ICLOCK-680ZMM-12', name: 'Fingerprint Terminal', description: 'S680 Fingerprint Time & Attendance', datasheet: 'https://customer-assets.emergentagent.com/job_biosecurity-hub/artifacts/ez3s484x_S680.pdf' },
    { id: 'face', sku: 'TA-EFACE-10-1-W', name: 'Face Recognition Terminal', description: 'EFace 10 Facial Recognition', datasheet: 'https://customer-assets.emergentagent.com/job_biosecurity-hub/artifacts/iqgpjaar_EFace%2010_Datasheet_202406.pdf' },
    { id: 'ruggedised', sku: 'TA-S922ZMM-O1-4G', name: 'Ruggedised Terminal', description: 'S922 IP65 Outdoor Terminal', datasheet: 'https://customer-assets.emergentagent.com/job_biosecurity-hub/artifacts/84gxsn4k_S922%20IP65.pdf' }
  ];
  
  // Calculate T&A license tier based on attendance points and user count
  const calculateTaTier = () => {
    const points = parseInt(taAttendancePoints) || 0;
    const users = parseInt(taUserCount) || 0;
    
    // Determine tier by attendance points
    let pointsTier = 1;
    if (points <= 5) pointsTier = 1;
    else if (points <= 10) pointsTier = 2;
    else if (points <= 25) pointsTier = 3;
    else if (points <= 50) pointsTier = 4;
    else if (points <= 75) pointsTier = 5;
    else if (points <= 100) pointsTier = 6;
    else pointsTier = 6; // Max tier for > 100
    
    // Determine tier by user count
    let usersTier = 1;
    if (users <= 2000) usersTier = 1; // Tier 1-2 support 2000 users
    else if (users <= 3000) usersTier = 3; // Tier 3+ support 3000 users
    else usersTier = 7; // Need to contact ZKTeco
    
    // If users > 2000, minimum tier is 3
    if (users > 2000 && usersTier < 3) usersTier = 3;
    
    return Math.max(pointsTier, usersTier);
  };
  
  // Handle T&A user count change with validation
  const handleTaUserCountChange = (value) => {
    setTaUserCount(value);
    const users = parseInt(value) || 0;
    if (users > 3000) {
      setShowTaContactDialog(true);
    }
  };
  
  // Handle T&A device selection
  const handleTaDeviceToggle = (deviceId) => {
    setTaSelectedDevices(prev => {
      const existing = prev.find(d => d.type === deviceId);
      if (existing) {
        return prev.filter(d => d.type !== deviceId);
      } else {
        return [...prev, { type: deviceId, quantity: 1 }];
      }
    });
  };
  
  // Update T&A device quantity
  const handleTaDeviceQuantity = (deviceId, quantity) => {
    setTaSelectedDevices(prev => 
      prev.map(d => d.type === deviceId ? { ...d, quantity: parseInt(quantity) || 1 } : d)
    );
  };
  
  // Role-based pricing visibility
  const isAdmin = userRole === 'admin' || userRole === 'zkteco_admin' || userRole === 'zkteco';
  const isDistributor = userRole === 'distributor' || userRole === 'distributor_admin';
  const isInstaller = userRole === 'installer' || userRole === 'trade';
  const isEndUser = userRole === 'end_user' || userRole === 'enduser';
  
  // Pricing visibility rules:
  // Admin/ZKTeco: sees all 3 tiers
  // Distributors: see Distributor + Installer pricing
  // Installers: see Installer + End User pricing
  // End Users: see End User pricing only
  const showDistributorPricing = isAdmin || isDistributor;
  const showInstallerPricing = isAdmin || isDistributor || isInstaller;
  const showEndUserPricing = isAdmin || isInstaller || isEndUser;
  
  // BOM/Quote pricing based on user role and quote target selection
  // ZKTeco staff can select who the quote is for (distributor or installer)
  // - If "distributor" selected: show Distributor Price + Installer Price
  // - If "installer" selected: show only Installer Price
  // Non-ZKTeco users: End users see "End User Price", everyone else sees "Installer Price"
  const showBomDistributorPrice = !installerOnlyCapture && ((isAdmin && quoteTarget === 'distributor') || isDistributor);
  const showBomInstallerPrice = isAdmin ? (quoteTarget === 'distributor' || quoteTarget === 'installer') : !isEndUser;
  const showBomEndUserPrice = isAdmin ? quoteTarget === 'end_user' : isEndUser;

  const handleCalculate = async () => {
    setError("");
    setResult(null);
    
    if (!projectName.trim()) {
      setError("Please enter a project name");
      return;
    }
    
    // Validate quote target for ZKTeco staff
    if (isAdmin && !quoteTarget) {
      setError("Please select who this quote is for (Distributor, Installer, or End User)");
      return;
    }

    // Validate Access Control module if selected
    if (accessControlSelected) {
      if (accessDoors === "" || isNaN(parseInt(accessDoors)) || parseInt(accessDoors) < 0) {
        setError("Please enter a valid number of access control doors (0 or more)");
        return;
      }

      if (doorsNum > 0) {
        if (!needsReadInOut) {
          setError("Please specify if Read In + Read Out is required");
          return;
        }
        if (needsReadInOut === "yes" && (!readInOutDoors || parseInt(readInOutDoors) < 1)) {
          setError("Please specify how many doors need Read In + Read Out");
          return;
        }
        if (needsReadInOut === "yes" && parseInt(readInOutDoors) > doorsNum) {
          setError("Read In + Read Out doors cannot exceed total doors");
          return;
        }
        if (selectedCredentials.length === 0) {
          setError("Please select a credential type");
          return;
        }
        
        // Validate external doors for Face/Palm
        if (hasFaceOrPalm && doorsNum > 0) {
          const extDoors = parseInt(externalDoors) || 0;
          if (extDoors > doorsNum) {
            setError("External doors cannot exceed total doors");
            return;
          }
        }
        
        // Validate Bluetooth credentials
        if (hasBluetooth) {
          if (!bluetoothLicenseType) {
            setError("Please select a Bluetooth license type (Annual or Permanent)");
            return;
          }
          if (!bluetoothUserCount || parseInt(bluetoothUserCount) < 1) {
            setError("Please enter the number of Bluetooth credentials required");
            return;
          }
        }
      }

      // Validate card printer
      if (needsCardPrinter === "yes" && (!printerCount || parseInt(printerCount) < 1)) {
        setError("Please specify the number of printers (1-5)");
        return;
      }

      // Validate QR mobile users
      if (hasQrCode && qrMobileUsers && parseInt(qrMobileUsers) < 0) {
        setError("Please enter a valid number of mobile app users");
        return;
      }
      
      // Validate emergency evacuation external doors
      if (needsEmergencyEvac === "yes" && doorsNum > 0) {
        const evacExtDoors = parseInt(evacExternalDoors) || 0;
        if (evacExtDoors > doorsNum) {
          setError("Emergency evacuation external doors cannot exceed total doors");
          return;
        }
      }
    }

    // Validate Time & Attendance module if selected
    if (timeAttendanceSelected) {
      if (!taAttendancePoints || parseInt(taAttendancePoints) < 1) {
        setError("Please enter the number of attendance points for Time & Attendance");
        return;
      }
      if (!taUserCount || parseInt(taUserCount) < 1) {
        setError("Please enter the number of users for Time & Attendance");
        return;
      }
      if (parseInt(taUserCount) > 3000) {
        setError("Time & Attendance supports up to 3,000 users. Please contact ZKTeco for larger deployments.");
        return;
      }
      if (!taDeviceType) {
        setError("Please select a device type for Time & Attendance clocking");
        return;
      }
      if (taDeviceType === 'dedicated' && taSelectedDevices.length === 0) {
        setError("Please select at least one dedicated Time & Attendance device");
        return;
      }
    }

    // Validate Visitor Management module if selected
    if (visitorManagementSelected) {
      if (!vmMonthlyVisitors) {
        setError("Please select anticipated monthly visitors for Visitor Management");
        return;
      }
      if (vmMonthlyVisitors === "20001+") {
        setError("For 20,001+ monthly visitors, please contact ZKTeco UK on 0121 790 1600 for a custom solution.");
        return;
      }
      if (!vmRegistrationSites) {
        setError("Please select number of registration sites for Visitor Management");
        return;
      }
      // Only require dedicated terminal answer if access doors is 0
      if (doorsNum === 0 && !vmDedicatedTerminal) {
        setError("Please specify if a dedicated access control terminal is needed for visitors");
        return;
      }
      if (!vmUnmannedReception) {
        setError("Please specify if you require unmanned reception for Visitor Management");
        return;
      }
    }

    setLoading(true);

    try {
      // Build request payload
      const payload = {
        project_name: projectName,
        access_doors: accessControlSelected ? doorsNum : 0,
        admin_licenses: parseInt(adminLicenses) || 0,
        read_in_out: needsReadInOut === "yes",
        read_in_out_doors: needsReadInOut === "yes" ? (parseInt(readInOutDoors) || 0) : 0,
        credentials: accessControlSelected ? selectedCredentials : [],
        external_doors: hasFaceOrPalm ? (parseInt(externalDoors) || 0) : 0,
        bluetooth_license_type: hasBluetooth ? bluetoothLicenseType : null,
        bluetooth_user_count: hasBluetooth ? (parseInt(bluetoothUserCount) || 0) : 0,
        card_printer: needsCardPrinter === "yes",
        printer_count: needsCardPrinter === "yes" ? (parseInt(printerCount) || 1) : 0,
        emergency_evacuation: needsEmergencyEvac === "yes",
        evac_external_doors: needsEmergencyEvac === "yes" ? (parseInt(evacExternalDoors) || 0) : 0,
        qr_mobile_users: hasQrCode ? (parseInt(qrMobileUsers) || 0) : 0,
        modules: selectedModules.filter(m => m !== 'access_control' && m !== 'time_attendance'),
        module_configs: {},
        // Time & Attendance configuration
        time_attendance: timeAttendanceSelected ? {
          attendance_points: parseInt(taAttendancePoints) || 0,
          user_count: parseInt(taUserCount) || 0,
          device_type: taDeviceType,
          selected_devices: taDeviceType === 'dedicated' ? taSelectedDevices : [],
          calculated_tier: calculateTaTier()
        } : null,
        // Visitor Management configuration
        visitor_management: visitorManagementSelected ? {
          monthly_visitors: vmMonthlyVisitors,
          registration_sites: parseInt(vmRegistrationSites) || 1,
          // If access doors > 0, dedicated terminal is not needed (use existing AC)
          dedicated_terminal: doorsNum === 0 ? vmDedicatedTerminal === "yes" : false,
          unmanned_reception: vmUnmannedReception === "yes"
        } : null
      };

      // Call backend API
      const response = await axios.post(`${API}/configure-biocv`, payload, getAuthHeaders());
      setResult(response.data);
      
      // Check if installer price >= £20,000 for project support (for distributors and admins)
      const installerPrice = response.data.final_installer_price || 0;
      if (installerPrice >= 20000 && !supportDeclined && (isDistributor || isAdmin)) {
        setShowProjectSupport(true);
      }

    } catch (err) {
      setError(err.response?.data?.detail || "Failed to calculate quote");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQuote = async (installerOnly = false) => {
    if (!quoteBoxRef.current) return;
    
    try {
      if (installerOnly) {
        setInstallerOnlyCapture(true);
        await new Promise(r => setTimeout(r, 250));
      }
      const canvas = await html2canvas(quoteBoxRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      const suffix = installerOnly ? 'Installer' : (isDistributor ? 'Distributor' : '');
      const link = document.createElement('a');
      link.download = `${projectName || 'BioCV'}${suffix ? '_' + suffix : ''}_Quote.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading quote:', err);
      setError('Failed to download quote image');
    } finally {
      if (installerOnly) setInstallerOnlyCapture(false);
    }
  };

  const handleNewQuote = () => {
    // Check if there's any data entered
    const hasData = projectName || selectedModules.length > 0 || accessDoors;
    
    // Check if current work is saved
    const hasResults = result !== null;
    
    if (hasData) {
      let message = "Start a new quote?";
      if (hasResults) {
        message = "Start a new quote?\n\nYour current quote appears to be calculated. Make sure it's saved if needed.";
      } else {
        message = "Start a new quote?\n\nAny unsaved changes will be lost.";
      }
      
      const confirmed = window.confirm(message);
      if (!confirmed) {
        return;
      }
    }
    
    // Reset all state to initial values
    setProjectName("");
    setSelectedModules([]);
    setAccessDoors("");
    setNeedsReadInOut("");
    setReadInOutDoors("");
    setSelectedCredentials([]);
    setExternalDoors("");
    setNeedsCardPrinter("");
    setPrinterCount("");
    setNeedsEmergencyEvac("");
    setEvacExternalDoors("");
    setQrMobileUsers("");
    setAdminLicenses("");
    setBluetoothLicenseType("");
    setBluetoothUserCount("");
    setResult(null);
    setError("");
    setSupportDeclined(false);
    // Reset quote target for ZKTeco staff
    setQuoteTarget("");
    // Reset advanced functionality
    setNeedsAdvancedFunctionality("");
    setSelectedAdvancedModules([]);
    // Reset Time & Attendance
    setTaAttendancePoints("");
    setTaUserCount("");
    setTaDeviceType("");
    setTaSelectedDevices([]);
    // Reset Visitor Management
    setVmMonthlyVisitors("");
    setVmRegistrationSites("");
    setVmDedicatedTerminal("");
    setVmUnmannedReception("");
    
    // Show success message
    setError("");
    alert("✓ New quote started! Enter project details below.");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm">{backLabel}</span>
          </Button>
        </div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Bio CVSecurity Configurator</h1>
          <p className="text-sm text-slate-500">Configure your Bio CVSecurity system</p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Section 1: Project Details */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Project Name <span className="text-red-500">*</span></Label>
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
              />
            </div>
            
            {/* Quote Target Selector - ZKTeco Staff Only */}
            <QuoteTargetSelector 
              quoteTarget={quoteTarget} 
              setQuoteTarget={setQuoteTarget} 
              userRole={userRole} 
            />
          </CardContent>
        </Card>

        {/* Section 2: Modules */}
        <Collapsible open={sectionsOpen.modules} onOpenChange={() => toggleSection('modules')}>
          <Card className="mb-4">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-green-600" />
                    <CardTitle className="text-lg">Modules</CardTitle>
                    {selectedModules.length > 0 && (
                      <Badge variant="secondary">{selectedModules.length} selected</Badge>
                    )}
                  </div>
                  {sectionsOpen.modules ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
                <CardDescription className="text-left">Select the modules you need</CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4">
                {ALL_MODULES.map((module) => (
                  <div key={module.id}>
                    <div 
                      className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                        selectedModules.includes(module.id) 
                          ? 'bg-blue-50 border-blue-300' 
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => handleModuleToggle(module.id)}
                    >
                      <Checkbox 
                        id={`module-${module.id}`}
                        checked={selectedModules.includes(module.id)}
                        onCheckedChange={() => {}} 
                        className="pointer-events-none"
                      />
                      <module.icon className="h-5 w-5 text-slate-600 mt-0.5" />
                      <div className="flex-1">
                        <Label htmlFor={`module-${module.id}`} className="font-medium cursor-pointer pointer-events-none">
                          {module.name}
                          {module.isMain && <Badge variant="outline" className="ml-2 text-xs">Core</Badge>}
                        </Label>
                        <p className="text-xs text-slate-500">{module.description}</p>
                      </div>
                    </div>
                    
                    {/* Access Control Sub-form */}
                    {module.id === 'access_control' && selectedModules.includes('access_control') && (
                      <div className="ml-8 mt-3 p-4 bg-slate-50 rounded-md border border-slate-200 space-y-4">
                        <h4 className="font-medium text-slate-700 flex items-center gap-2">
                          <DoorOpen className="h-4 w-4" />
                          Access Control Configuration
                        </h4>
                        
                        {/* Number of Doors */}
                        <div className="space-y-2">
                          <Label>How many doors for Access Control? <span className="text-red-500">*</span></Label>
                          <p className="text-xs text-slate-500">Enter 0 if you do not need access control doors</p>
                          <Input
                            type="number"
                            min="0"
                            max="12000"
                            value={accessDoors}
                            onChange={(e) => setAccessDoors(e.target.value)}
                            placeholder="e.g., 10"
                            className="max-w-xs"
                          />
                          <p className="text-xs text-slate-500">Minimum 5-door license required regardless of door count</p>
                        </div>

                        {/* Read In/Out Question */}
                        {doorsNum > 0 && (
                          <>
                            <div className="space-y-2">
                              <Label>Is Read In + Read Out required? <span className="text-red-500">*</span></Label>
                              <p className="text-xs text-slate-500">Read In only = 1 reader per door. Read In + Read Out = 2 readers per door</p>
                              <RadioGroup 
                                value={needsReadInOut} 
                                onValueChange={setNeedsReadInOut}
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="yes" id="rio-yes" />
                                  <Label htmlFor="rio-yes" className="font-normal cursor-pointer">Yes</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="no" id="rio-no" />
                                  <Label htmlFor="rio-no" className="font-normal cursor-pointer">No (Read In + Free Exit)</Label>
                                </div>
                              </RadioGroup>
                            </div>

                            {needsReadInOut === "yes" && (
                              <div className="space-y-2">
                                <Label>How many doors need Read In + Read Out?</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max={doorsNum}
                                  value={readInOutDoors}
                                  onChange={(e) => setReadInOutDoors(e.target.value)}
                                  placeholder={`1-${doorsNum}`}
                                  className="max-w-xs"
                                />
                              </div>
                            )}

                            {/* Credential Type Selection */}
                            <div className="space-y-2">
                              <Label>Select credential type <span className="text-red-500">*</span></Label>
                              <p className="text-xs text-slate-500">Choose one credential type for your readers</p>
                              <RadioGroup 
                                value={selectedCredentials[0] || ""} 
                                onValueChange={handleCredentialSelect}
                                className="grid grid-cols-1 md:grid-cols-2 gap-2"
                              >
                                {CREDENTIAL_TYPES.map((cred) => (
                                  <div 
                                    key={cred.id}
                                    className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      selectedCredentials.includes(cred.id) 
                                        ? 'bg-blue-50 border-blue-300' 
                                        : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                    onClick={() => handleCredentialSelect(cred.id)}
                                  >
                                    <RadioGroupItem 
                                      value={cred.id}
                                      id={`cred-${cred.id}`}
                                    />
                                    <div className="flex-1">
                                      <Label htmlFor={`cred-${cred.id}`} className="font-medium cursor-pointer">{cred.name}</Label>
                                      <p className="text-xs text-slate-500">{cred.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </RadioGroup>
                            </div>

                            {/* QR Code Mobile Users - Only show if QR Code is selected */}
                            {hasQrCode && (
                              <div className="space-y-2 p-3 bg-amber-50 rounded-md border border-amber-200">
                                <Label className="flex items-center gap-2">
                                  <Users className="h-4 w-4 text-amber-600" />
                                  How many users need mobile app access?
                                </Label>
                                <p className="text-xs text-slate-600">Employee mobile app licenses are tiered: £5.40/user (0-500) or £3.90/user (500+)</p>
                                <Input
                                  type="number"
                                  min="0"
                                  value={qrMobileUsers}
                                  onChange={(e) => setQrMobileUsers(e.target.value)}
                                  placeholder="e.g., 100"
                                  className="max-w-xs"
                                />
                              </div>
                            )}
                            
                            {/* Bluetooth Credentials - Only show if Bluetooth is selected */}
                            {hasBluetooth && (
                              <div className="space-y-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                                <Label className="flex items-center gap-2 font-medium">
                                  <Radio className="h-4 w-4 text-blue-600" />
                                  Bluetooth Credential Configuration
                                </Label>
                                
                                {/* License Type Selection */}
                                <div className="space-y-2">
                                  <Label>License Type <span className="text-red-500">*</span></Label>
                                  <RadioGroup 
                                    value={bluetoothLicenseType} 
                                    onValueChange={setBluetoothLicenseType}
                                    className="space-y-2"
                                  >
                                    <div className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      bluetoothLicenseType === 'annual' 
                                        ? 'bg-green-50 border-green-300' 
                                        : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                    onClick={() => setBluetoothLicenseType('annual')}
                                    >
                                      <RadioGroupItem value="annual" id="bt-annual" />
                                      <div className="flex-1">
                                        <Label htmlFor="bt-annual" className="font-medium cursor-pointer">Annual (Reassignable)</Label>
                                        <p className="text-xs text-slate-500">Credential can be reassigned multiple times. Best for staff turnover.</p>
                                      </div>
                                    </div>
                                    <div className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      bluetoothLicenseType === 'permanent' 
                                        ? 'bg-green-50 border-green-300' 
                                        : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                    onClick={() => setBluetoothLicenseType('permanent')}
                                    >
                                      <RadioGroupItem value="permanent" id="bt-permanent" />
                                      <div className="flex-1">
                                        <Label htmlFor="bt-permanent" className="font-medium cursor-pointer">Permanent (Device-Tied)</Label>
                                        <p className="text-xs text-slate-500">Credential is permanently tied to one device and cannot be reassigned.</p>
                                      </div>
                                    </div>
                                  </RadioGroup>
                                </div>
                                
                                {/* User Count Input */}
                                <div className="space-y-2">
                                  <Label>How many Bluetooth credentials do you need? <span className="text-red-500">*</span></Label>
                                  <p className="text-xs text-slate-600">Enter the total number of users who will need mobile Bluetooth access.</p>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={bluetoothUserCount}
                                    onChange={(e) => setBluetoothUserCount(e.target.value)}
                                    placeholder="e.g., 80"
                                    className="max-w-xs"
                                    data-testid="bluetooth-user-count-input"
                                  />
                                  {bluetoothUserCount && parseInt(bluetoothUserCount) > 0 && (
                                    <p className="text-xs text-blue-600">
                                      The system will automatically select the best license tier to cover {bluetoothUserCount} users.
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* External Doors - Show when Face or Palm is selected */}
                        {hasFaceOrPalm && doorsNum > 0 && (
                          <div className="space-y-2 p-3 bg-amber-50 rounded-md border border-amber-200">
                            <Label className="flex items-center gap-2">
                              <DoorOpen className="h-4 w-4 text-amber-600" />
                              How many doors are external? <span className="text-red-500">*</span>
                            </Label>
                            <p className="text-xs text-slate-600">External doors require weatherproof terminals (IP65 rated)</p>
                            <Input
                              id="externalDoors"
                              type="number"
                              min="0"
                              max={doorsNum}
                              value={externalDoors}
                              onChange={(e) => setExternalDoors(e.target.value)}
                              placeholder={`0-${doorsNum}`}
                              className="max-w-xs bg-white"
                            />
                            {externalDoors && parseInt(externalDoors) > 0 && (
                              <p className="text-xs text-amber-700">
                                {parseInt(externalDoors)} external, {doorsNum - parseInt(externalDoors)} internal
                              </p>
                            )}
                          </div>
                        )}

                        {/* Card Printer */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Printer className="h-4 w-4" />
                            Do you need card printing functionality? <span className="text-slate-500 font-normal">(For printing purposes only, no encoding functionality)</span>
                          </Label>
                          <RadioGroup 
                            value={needsCardPrinter} 
                            onValueChange={setNeedsCardPrinter}
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="yes" id="printer-yes" />
                              <Label htmlFor="printer-yes" className="font-normal cursor-pointer">Yes</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no" id="printer-no" />
                              <Label htmlFor="printer-no" className="font-normal cursor-pointer">No</Label>
                            </div>
                          </RadioGroup>
                        </div>

                        {needsCardPrinter === "yes" && (
                          <div className="space-y-2">
                            <Label>How many printers? (1-5)</Label>
                            <p className="text-xs text-slate-500">1 printer uses P1 license, 2-5 printers uses P5 license</p>
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              value={printerCount}
                              onChange={(e) => setPrinterCount(e.target.value)}
                              placeholder="1-5"
                              className="max-w-xs"
                            />
                          </div>
                        )}

                        {/* Emergency Evacuation */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-orange-500" />
                            Do you need emergency evacuation (Muster Point)?
                          </Label>
                          <p className="text-xs text-slate-500">Adds SOF-ZKBIOCV-AC-MP license for emergency assembly tracking</p>
                          <RadioGroup 
                            value={needsEmergencyEvac} 
                            onValueChange={(value) => {
                              setNeedsEmergencyEvac(value);
                              if (value === 'no') {
                                setEvacExternalDoors("");
                              }
                            }}
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="yes" id="evac-yes" />
                              <Label htmlFor="evac-yes" className="font-normal cursor-pointer">Yes</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="no" id="evac-no" />
                              <Label htmlFor="evac-no" className="font-normal cursor-pointer">No</Label>
                            </div>
                          </RadioGroup>
                        </div>

                        {/* Emergency Evacuation External Doors */}
                        {needsEmergencyEvac === "yes" && doorsNum > 0 && (
                          <div className="space-y-2 p-3 bg-orange-50 rounded-md border border-orange-200">
                            <Label className="flex items-center gap-2">
                              <DoorOpen className="h-4 w-4 text-orange-600" />
                              How many of these doors are external (muster point exits)? <span className="text-red-500">*</span>
                            </Label>
                            <div className="p-2 bg-amber-100 rounded border border-amber-300 mb-2">
                              <p className="text-xs text-amber-800 font-medium">
                                ⚠️ Important: Read In + Read Out is mandatory for external doors with Emergency Evacuation to accurately track personnel leaving the building during an emergency.
                              </p>
                            </div>
                            <Input
                              type="number"
                              min="0"
                              max={doorsNum}
                              value={evacExternalDoors}
                              onChange={(e) => setEvacExternalDoors(e.target.value)}
                              placeholder={`0-${doorsNum}`}
                              className="max-w-xs bg-white"
                            />
                            {evacExternalDoors && parseInt(evacExternalDoors) > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs text-orange-700">
                                  {parseInt(evacExternalDoors)} external muster point door(s) will require Read In + Read Out capability
                                </p>
                                {/* Button to add doors to Read In + Read Out count */}
                                {(() => {
                                  const evacExtNum = parseInt(evacExternalDoors) || 0;
                                  const currentRioCount = parseInt(readInOutDoors) || 0;
                                  const needsMore = needsReadInOut !== "yes" || currentRioCount < evacExtNum;
                                  
                                  if (needsMore) {
                                    return (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="bg-white border-orange-400 text-orange-700 hover:bg-orange-100"
                                        onClick={() => {
                                          setNeedsReadInOut("yes");
                                          // Set to max of current count or evac external doors
                                          const newCount = Math.max(currentRioCount, evacExtNum);
                                          setReadInOutDoors(newCount.toString());
                                        }}
                                      >
                                        Add {evacExtNum} door{evacExtNum > 1 ? 's' : ''} to Read In + Read Out count
                                      </Button>
                                    );
                                  } else {
                                    return (
                                      <p className="text-xs text-green-600 font-medium">
                                        ✓ Read In + Read Out is configured for {currentRioCount} door(s)
                                      </p>
                                    );
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Admin App Licenses */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-blue-500" />
                            How many administrator app licenses do you require?
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={adminLicenses}
                            onChange={(e) => setAdminLicenses(e.target.value)}
                            placeholder="e.g., 2"
                            className="max-w-xs"
                          />
                          <p className="text-xs text-slate-500">Each admin gets a mobile app license (SOF-BIOCV-MBAPP-N-ADMIN-ADDON)</p>
                        </div>

                        {/* Advanced Functionality Section */}
                        <div className="mt-6 pt-4 border-t border-slate-200 space-y-4">
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <Settings className="h-4 w-4 text-blue-500" />
                              Do you require any advanced functionality?
                            </Label>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant={needsAdvancedFunctionality === 'yes' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleAdvancedFunctionalityChange('yes')}
                                className={needsAdvancedFunctionality === 'yes' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                              >
                                Yes
                              </Button>
                              <Button
                                type="button"
                                variant={needsAdvancedFunctionality === 'no' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleAdvancedFunctionalityChange('no')}
                                className={needsAdvancedFunctionality === 'no' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                              >
                                No
                              </Button>
                            </div>
                          </div>
                          
                          {/* Always visible list of available modules */}
                          <div className={`space-y-2 p-4 rounded-lg border ${needsAdvancedFunctionality === 'yes' ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                            <p className="text-sm font-medium text-slate-700">Available Advanced Modules:</p>
                            <div className="space-y-2">
                              {ADVANCED_MODULES.map((module) => {
                                const ModuleIcon = module.icon;
                                const isSelected = selectedAdvancedModules.includes(module.id);
                                const isDisabled = needsAdvancedFunctionality !== 'yes';
                                // Modules that are ready (have full configurators)
                                const readyModules = ['time_attendance', 'visitor_management'];
                                const isReady = readyModules.includes(module.id);
                                
                                return (
                                  <div key={module.id} className="space-y-1">
                                    <div 
                                      className={`flex items-center gap-3 p-2 rounded-md border transition-colors ${
                                        isDisabled 
                                          ? 'bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed' 
                                          : isSelected 
                                            ? 'bg-white border-blue-400 cursor-pointer' 
                                            : 'bg-white border-slate-200 hover:bg-slate-50 cursor-pointer'
                                      }`}
                                      onClick={() => !isDisabled && handleAdvancedModuleToggle(module.id)}
                                    >
                                      <Checkbox 
                                        id={`inline-adv-${module.id}`}
                                        checked={isSelected}
                                        disabled={isDisabled}
                                        onCheckedChange={() => !isDisabled && handleAdvancedModuleToggle(module.id)}
                                      />
                                      <ModuleIcon className={`h-4 w-4 ${isDisabled ? 'text-slate-400' : 'text-blue-600'}`} />
                                      <div className="flex-1">
                                        <Label htmlFor={`inline-adv-${module.id}`} className={`text-sm font-medium ${isDisabled ? 'text-slate-500 cursor-not-allowed' : 'cursor-pointer'}`}>
                                          {module.name}
                                        </Label>
                                        <p className="text-xs text-slate-500">{module.description}</p>
                                      </div>
                                    </div>
                                    {/* Show "coming soon" warning inline when selected but not ready */}
                                    {isSelected && !isReady && (
                                      <div className="ml-7 p-2 bg-amber-50 border border-amber-300 rounded-md animate-pulse">
                                        <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                                          <AlertTriangle className="h-3 w-3" />
                                          Configuration coming soon - will be included in quote. Contact ZKTeco for specific pricing.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {needsAdvancedFunctionality !== 'yes' && (
                              <p className="text-xs text-slate-500 italic mt-2">Select &quot;Yes&quot; above to enable module selection</p>
                            )}
                          </div>
                          
                          {/* Time & Attendance Configuration (shown when selected) */}
                          {needsAdvancedFunctionality === 'yes' && selectedAdvancedModules.includes('time_attendance') && (
                            <div className="p-4 bg-teal-50 rounded-lg border border-teal-200 space-y-4">
                              <h4 className="font-medium text-teal-700 flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Time & Attendance Configuration
                              </h4>
                              
                              {/* Number of Attendance Points */}
                              <div className="space-y-2">
                                <Label>How many attendance points do you need? <span className="text-red-500">*</span></Label>
                                <p className="text-xs text-slate-500">An attendance point is a location where employees clock in/out</p>
                                <Input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={taAttendancePoints}
                                  onChange={(e) => setTaAttendancePoints(e.target.value)}
                                  placeholder="e.g., 5"
                                  className="max-w-xs"
                                  data-testid="ta-attendance-points-input"
                                />
                              </div>
                              
                              {/* Number of Users */}
                              <div className="space-y-2">
                                <Label>How many users? <span className="text-red-500">*</span></Label>
                                <p className="text-xs text-slate-500">Total number of employees using time & attendance</p>
                                <Input
                                  type="number"
                                  min="1"
                                  value={taUserCount}
                                  onChange={(e) => handleTaUserCountChange(e.target.value)}
                                  placeholder="e.g., 500"
                                  className="max-w-xs"
                                  data-testid="ta-user-count-input"
                                />
                                {taUserCount && parseInt(taUserCount) > 0 && parseInt(taUserCount) <= 3000 && (
                                  <p className="text-xs text-blue-600">
                                    {parseInt(taUserCount) <= 2000 
                                      ? `Tier 1-2 licenses support up to 2,000 users.`
                                      : `Tier 3+ licenses required for ${taUserCount} users (supports up to 3,000).`
                                    }
                                  </p>
                                )}
                              </div>
                              
                              {/* Show calculated tier */}
                              {taAttendancePoints && taUserCount && parseInt(taUserCount) <= 3000 && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                  <p className="text-sm text-green-700">
                                    <strong>Selected License:</strong> Tier {calculateTaTier()} - 
                                    {calculateTaTier() === 1 && " 0-5 points, 2,000 users"}
                                    {calculateTaTier() === 2 && " 6-10 points, 2,000 users"}
                                    {calculateTaTier() === 3 && " 11-25 points, 3,000 users"}
                                    {calculateTaTier() === 4 && " 26-50 points, 3,000 users"}
                                    {calculateTaTier() === 5 && " 51-75 points, 3,000 users"}
                                    {calculateTaTier() === 6 && " 76-100 points, 3,000 users"}
                                  </p>
                                </div>
                              )}
                              
                              {/* Device Type Selection */}
                              {taAttendancePoints && taUserCount && parseInt(taUserCount) <= 3000 && (
                                <div className="space-y-3">
                                  <Label>What type of device will be used for clocking? <span className="text-red-500">*</span></Label>
                                  <RadioGroup 
                                    value={taDeviceType} 
                                    onValueChange={setTaDeviceType}
                                    className="space-y-2"
                                  >
                                    <div className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      taDeviceType === 'access_control' 
                                        ? 'bg-blue-50 border-blue-300' 
                                        : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                    onClick={() => setTaDeviceType('access_control')}
                                    >
                                      <RadioGroupItem value="access_control" id="ta-device-ac-inline" />
                                      <div className="flex-1">
                                        <Label htmlFor="ta-device-ac-inline" className="font-medium cursor-pointer">Access Control Terminal</Label>
                                        <p className="text-xs text-slate-500">Use existing access control readers for clocking in/out</p>
                                      </div>
                                    </div>
                                    <div className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      taDeviceType === 'dedicated' 
                                        ? 'bg-blue-50 border-blue-300' 
                                        : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                    onClick={() => setTaDeviceType('dedicated')}
                                    >
                                      <RadioGroupItem value="dedicated" id="ta-device-dedicated-inline" />
                                      <div className="flex-1">
                                        <Label htmlFor="ta-device-dedicated-inline" className="font-medium cursor-pointer">Dedicated Time & Attendance Device</Label>
                                        <p className="text-xs text-slate-500">Use specialized T&A terminals (RFID, Fingerprint, Face, Ruggedised)</p>
                                      </div>
                                    </div>
                                  </RadioGroup>
                                </div>
                              )}
                              
                              {/* Dedicated Device Selection */}
                              {taDeviceType === 'dedicated' && (
                                <div className="space-y-3 p-3 bg-purple-50 rounded-md border border-purple-200">
                                  <Label className="font-medium">Select Time & Attendance Devices</Label>
                                  <p className="text-xs text-slate-600">Choose which devices you need and specify quantities</p>
                                  
                                  <div className="space-y-2">
                                    {TA_DEVICES.map((device) => {
                                      const isDeviceSelected = taSelectedDevices.some(d => d.type === device.id);
                                      const selectedDevice = taSelectedDevices.find(d => d.type === device.id);
                                      
                                      return (
                                        <div key={device.id} className={`p-3 rounded-md border transition-colors ${
                                          isDeviceSelected ? 'bg-white border-purple-400' : 'bg-white border-slate-200'
                                        }`}>
                                          <div className="flex items-start gap-3">
                                            <Checkbox 
                                              id={`ta-device-inline-${device.id}`}
                                              checked={isDeviceSelected}
                                              onCheckedChange={() => handleTaDeviceToggle(device.id)}
                                            />
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <Label htmlFor={`ta-device-inline-${device.id}`} className="font-medium cursor-pointer">
                                                  {device.name}
                                                </Label>
                                                <a 
                                                  href={device.datasheet} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <FileText className="h-3 w-3" />
                                                  Datasheet
                                                </a>
                                              </div>
                                              <p className="text-xs text-slate-500">{device.description}</p>
                                              <p className="text-xs text-slate-400 font-mono">{device.sku}</p>
                                            </div>
                                            
                                            {isDeviceSelected && (
                                              <div className="flex items-center gap-2">
                                                <Label className="text-xs">Qty:</Label>
                                                <Input
                                                  type="number"
                                                  min="1"
                                                  value={selectedDevice?.quantity || 1}
                                                  onChange={(e) => handleTaDeviceQuantity(device.id, e.target.value)}
                                                  className="w-20 h-8 text-sm"
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Visitor Management Configuration (shown when selected) */}
                          {needsAdvancedFunctionality === 'yes' && selectedAdvancedModules.includes('visitor_management') && (
                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                              <h4 className="font-medium text-purple-700 flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Visitor Management Configuration
                              </h4>
                              
                              {/* Monthly Visitors */}
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <Users className="h-4 w-4 text-purple-600" />
                                  Anticipated Monthly Visitors <span className="text-red-500">*</span>
                                </Label>
                                <p className="text-xs text-slate-500">Select the expected number of visitors per month</p>
                                <RadioGroup value={vmMonthlyVisitors} onValueChange={setVmMonthlyVisitors} className="space-y-2">
                                  <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                    vmMonthlyVisitors === "0-5000" ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                  }`} onClick={() => setVmMonthlyVisitors("0-5000")}>
                                    <RadioGroupItem value="0-5000" id="vm-visitors-1" />
                                    <Label htmlFor="vm-visitors-1" className="cursor-pointer flex-1">0 - 5,000 visitors</Label>
                                  </div>
                                  <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                    vmMonthlyVisitors === "5001-10000" ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                  }`} onClick={() => setVmMonthlyVisitors("5001-10000")}>
                                    <RadioGroupItem value="5001-10000" id="vm-visitors-2" />
                                    <Label htmlFor="vm-visitors-2" className="cursor-pointer flex-1">5,001 - 10,000 visitors</Label>
                                  </div>
                                  <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                    vmMonthlyVisitors === "10001-20000" ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                  }`} onClick={() => setVmMonthlyVisitors("10001-20000")}>
                                    <RadioGroupItem value="10001-20000" id="vm-visitors-3" />
                                    <Label htmlFor="vm-visitors-3" className="cursor-pointer flex-1">10,001 - 20,000 visitors</Label>
                                  </div>
                                  <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                    vmMonthlyVisitors === "20001+" ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                                  }`} onClick={() => {
                                    setVmMonthlyVisitors("20001+");
                                    setShowVmContactDialog(true);
                                  }}>
                                    <RadioGroupItem value="20001+" id="vm-visitors-4" />
                                    <Label htmlFor="vm-visitors-4" className="cursor-pointer flex-1">20,001+ visitors (Contact ZKTeco)</Label>
                                  </div>
                                </RadioGroup>
                              </div>

                              {/* Registration Sites - Only show if not 20001+ */}
                              {vmMonthlyVisitors && vmMonthlyVisitors !== "20001+" && (
                                <div className="space-y-2">
                                  <Label className="flex items-center gap-2">
                                    <Monitor className="h-4 w-4 text-purple-600" />
                                    Number of Registration Sites <span className="text-red-500">*</span>
                                  </Label>
                                  <p className="text-xs text-slate-500">A workstation is considered a registration site</p>
                                  <RadioGroup value={vmRegistrationSites} onValueChange={setVmRegistrationSites} className="grid grid-cols-5 gap-2">
                                    {[1, 2, 3, 4, 5].map((num) => (
                                      <div key={num} className={`flex items-center justify-center p-3 rounded-md border cursor-pointer transition-colors ${
                                        vmRegistrationSites === String(num) ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                      }`} onClick={() => setVmRegistrationSites(String(num))}>
                                        <RadioGroupItem value={String(num)} id={`vm-sites-${num}`} className="sr-only" />
                                        <Label htmlFor={`vm-sites-${num}`} className="cursor-pointer font-medium">{num}</Label>
                                      </div>
                                    ))}
                                  </RadioGroup>
                                </div>
                              )}

                              {/* Dedicated Terminal Question - Only show if access doors is 0 */}
                              {vmMonthlyVisitors && vmMonthlyVisitors !== "20001+" && vmRegistrationSites && doorsNum === 0 && (
                                <div className="space-y-2">
                                  <Label>Do you want to use a dedicated access control terminal to check in? <span className="text-red-500">*</span></Label>
                                  <p className="text-xs text-slate-500">This adds a 5-door access license for visitor terminal integration</p>
                                  <RadioGroup value={vmDedicatedTerminal} onValueChange={setVmDedicatedTerminal} className="space-y-2">
                                    <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      vmDedicatedTerminal === "yes" ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`} onClick={() => setVmDedicatedTerminal("yes")}>
                                      <RadioGroupItem value="yes" id="vm-terminal-yes" />
                                      <Label htmlFor="vm-terminal-yes" className="cursor-pointer flex-1">Yes</Label>
                                    </div>
                                    <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      vmDedicatedTerminal === "no" ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`} onClick={() => setVmDedicatedTerminal("no")}>
                                      <RadioGroupItem value="no" id="vm-terminal-no" />
                                      <Label htmlFor="vm-terminal-no" className="cursor-pointer flex-1">No</Label>
                                    </div>
                                  </RadioGroup>
                                </div>
                              )}

                              {/* Unmanned Reception Question - Show after registration sites are selected */}
                              {vmMonthlyVisitors && vmMonthlyVisitors !== "20001+" && vmRegistrationSites && (
                                <div className="space-y-2">
                                  <Label>Do you require Unmanned Reception? <span className="text-red-500">*</span></Label>
                                  <p className="text-xs text-slate-500">Adds a FaceKiosk H13C terminal for each registration site for self-service visitor check-in</p>
                                  <RadioGroup value={vmUnmannedReception} onValueChange={setVmUnmannedReception} className="space-y-2">
                                    <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      vmUnmannedReception === "yes" ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`} onClick={() => setVmUnmannedReception("yes")}>
                                      <RadioGroupItem value="yes" id="vm-unmanned-yes" />
                                      <Label htmlFor="vm-unmanned-yes" className="cursor-pointer flex-1">Yes</Label>
                                    </div>
                                    <div className={`flex items-center space-x-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                      vmUnmannedReception === "no" ? 'bg-white border-purple-400' : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`} onClick={() => setVmUnmannedReception("no")}>
                                      <RadioGroupItem value="no" id="vm-unmanned-no" />
                                      <Label htmlFor="vm-unmanned-no" className="cursor-pointer flex-1">No</Label>
                                    </div>
                                  </RadioGroup>
                                  {vmUnmannedReception === "yes" && (
                                    <p className="text-xs text-purple-600 font-medium">
                                      {vmRegistrationSites} x FaceKiosk H13C will be added to the quote
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Show summary when all fields are filled */}
                              {vmMonthlyVisitors && vmMonthlyVisitors !== "20001+" && vmRegistrationSites && vmUnmannedReception && (doorsNum > 0 || vmDedicatedTerminal) && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                  <p className="text-sm text-green-700">
                                    <strong>Configuration Summary:</strong><br />
                                    • Monthly Visitors: {vmMonthlyVisitors}<br />
                                    • Registration Sites: {vmRegistrationSites}<br />
                                    {doorsNum === 0 ? (
                                      <>• Dedicated Terminal: {vmDedicatedTerminal === "yes" ? "Yes" : "No (visitors chaperoned)"}<br /></>
                                    ) : (
                                      <>• Using existing Access Control ({doorsNum} doors)<br /></>
                                    )}
                                    • Unmanned Reception: {vmUnmannedReception === "yes" ? `Yes (${vmRegistrationSites} FaceKiosk)` : "No"}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Contact ZKTeco Dialog for 20001+ visitors */}
                          <Dialog open={showVmContactDialog} onOpenChange={setShowVmContactDialog}>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-purple-700">
                                  <Phone className="h-5 w-5" />
                                  Contact ZKTeco UK
                                </DialogTitle>
                              </DialogHeader>
                              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <p className="text-slate-700 leading-relaxed">
                                  For deployments expecting more than 20,000 monthly visitors, we recommend a custom enterprise solution to ensure optimal performance and support.
                                </p>
                                <p className="text-slate-700 mt-4 font-medium">
                                  Please contact ZKTeco UK on <span className="text-purple-700 font-bold">0121 790 1600</span>
                                </p>
                                <p className="text-slate-600 mt-4 text-sm italic">
                                  Thank you for choosing ZKTeco
                                </p>
                              </div>
                              <div className="flex justify-end mt-4">
                                <Button onClick={() => setShowVmContactDialog(false)}>
                                  Close
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    )}
                    
                    {/* Placeholder for other modules (not Access Control) - should not appear now since all modules moved to advanced */}
                    {module.id !== 'access_control' && selectedModules.includes(module.id) && (
                      <div className="ml-8 mt-3 p-3 bg-amber-50 rounded-md border border-amber-200">
                        <p className="text-sm text-amber-700 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          {module.name} configuration coming soon. Please consult ZKTeco for pricing.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Action Buttons */}
        <div className="flex justify-between mb-4">
          <Button 
            variant="outline"
            onClick={handleNewQuote}
            data-testid="new-quote-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Quote
          </Button>
          <Button 
            onClick={handleCalculate}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Calculator className="h-4 w-4 mr-2" />
            {loading ? "Calculating..." : "Calculate and Save"}
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div ref={quoteBoxRef}>
            <Card className="mt-6 border-green-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-green-700">Quote Summary</CardTitle>
                  <CardDescription>Configuration for {result.project_name}</CardDescription>
                </div>
                <div className="flex gap-2" data-html2canvas-ignore="true">
                  {isDistributor ? (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        data-testid="download-distributor-quote-btn"
                        onClick={() => handleDownloadQuote(false)}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Distributor Quote
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        data-testid="download-installer-quote-btn"
                        onClick={() => handleDownloadQuote(true)}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Installer Quote
                      </Button>
                    </>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDownloadQuote(false)}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download Quote
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleAddSundryItem}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Sundry Line
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Caveats/Notes */}
                {result.server_hosting_note && (
                  <Alert className="bg-orange-50 border-orange-200">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-700">
                      {result.server_hosting_note}
                    </AlertDescription>
                  </Alert>
                )}
                {result.face_palm_note && (
                  <Alert className="bg-orange-50 border-orange-200">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-700">
                      {result.face_palm_note}
                    </AlertDescription>
                  </Alert>
                )}

                {/* BOM Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2">SKU</th>
                        <th className="text-left p-2">Description</th>
                        <th className="text-center p-2">Qty</th>
                        <th className="text-center p-2">Datasheet</th>
                        {showBomDistributorPrice && (
                          <>
                            <th className="text-right p-2 text-purple-700">Distributor Price</th>
                            <th className="text-right p-2 text-purple-700">Total</th>
                          </>
                        )}
                        {showBomInstallerPrice && (
                          <>
                            <th className="text-right p-2 text-green-700">Installer Price</th>
                            <th className="text-right p-2 text-green-700">Total</th>
                          </>
                        )}
                        {showBomEndUserPrice && (
                          <>
                            <th className="text-right p-2 text-blue-700">End User Price</th>
                            <th className="text-right p-2 text-blue-700">Total</th>
                          </>
                        )}
                        <th className="text-center p-2 print:hidden"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.bom?.map((item, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-2 font-mono text-xs">
                            {item.product_url ? (
                              <a 
                                href={item.product_url.startsWith('http') ? item.product_url : `${BACKEND_URL}${item.product_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                title="View datasheet"
                              >
                                {item.sku}
                              </a>
                            ) : (
                              item.sku
                            )}
                          </td>
                          <td className="p-2">{item.description}</td>
                          <td className="p-2 text-center">{item.quantity}</td>
                          <td className="p-2 text-center">
                            {item.product_url && (
                              <a 
                                href={item.product_url.startsWith('http') ? item.product_url : `${BACKEND_URL}${item.product_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title="View datasheet"
                              >
                                <FileText className="h-4 w-4 inline" />
                              </a>
                            )}
                          </td>
                          {showBomDistributorPrice && (
                            <>
                              <td className="p-2 text-right text-purple-700">£{item.unit_price?.toFixed(2)}</td>
                              <td className="p-2 text-right text-purple-700 font-medium">£{item.line_total?.toFixed(2)}</td>
                            </>
                          )}
                          {showBomInstallerPrice && (
                            <>
                              <td className="p-2 text-right text-green-700">£{item.installer_price?.toFixed(2)}</td>
                              <td className="p-2 text-right text-green-700 font-medium">£{item.installer_total?.toFixed(2)}</td>
                            </>
                          )}
                          {showBomEndUserPrice && (
                            <>
                              <td className="p-2 text-right text-blue-700">£{item.enduser_price?.toFixed(2)}</td>
                              <td className="p-2 text-right text-blue-700 font-medium">£{item.enduser_total?.toFixed(2)}</td>
                            </>
                          )}
                          <td className="print:hidden"></td>
                        </tr>
                      ))}
                      {/* Sundry Items in B.O.M */}
                      {sundryItems.map((item, index) => (
                        <tr key={`sundry-${index}`} className="border-b border-amber-200 bg-amber-50">
                          <td className="p-2 font-mono text-xs text-amber-800">{item.sku || 'SUNDRY'}</td>
                          <td className="p-2 text-amber-800">{item.description}</td>
                          <td className="p-2 text-center text-amber-900">{item.quantity}</td>
                          <td className="p-2 text-center text-amber-700">Sundry</td>
                          {showBomDistributorPrice && (
                            <>
                              <td className="p-2 text-right text-amber-700">£{item.unit_price?.toFixed(2)}</td>
                              <td className="p-2 text-right font-medium text-amber-700">£{item.line_total?.toFixed(2)}</td>
                            </>
                          )}
                          {showBomInstallerPrice && (
                            <>
                              <td className="p-2 text-right text-amber-700">£{item.unit_price?.toFixed(2)}</td>
                              <td className="p-2 text-right font-medium text-amber-700">£{item.line_total?.toFixed(2)}</td>
                            </>
                          )}
                          {showBomEndUserPrice && (
                            <>
                              <td className="p-2 text-right text-amber-700">£{item.unit_price?.toFixed(2)}</td>
                              <td className="p-2 text-right font-medium text-amber-700">£{item.line_total?.toFixed(2)}</td>
                            </>
                          )}
                          <td className="p-2 text-center print:hidden">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-amber-700 hover:text-amber-900"
                                onClick={() => handleEditSundryItem(index)}
                              >
                                <FileText className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteSundryItem(index)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 font-medium">
                      <tr>
                        <td colSpan="4" className="p-2 text-right">Totals:</td>
                        {showBomDistributorPrice && (
                          <>
                            <td className="p-2"></td>
                            <td className="p-2 text-right text-purple-700 font-bold">£{result.trade_subtotal?.toFixed(2)}</td>
                          </>
                        )}
                        {showBomInstallerPrice && (
                          <>
                            <td className="p-2"></td>
                            <td className="p-2 text-right text-green-700 font-bold">£{result.installer_subtotal?.toFixed(2)}</td>
                          </>
                        )}
                        {showBomEndUserPrice && (
                          <>
                            <td className="p-2"></td>
                            <td className="p-2 text-right text-blue-700 font-bold">£{result.enduser_subtotal?.toFixed(2)}</td>
                          </>
                        )}
                      </tr>
                      {sundryItems.length > 0 && (
                        <>
                          <tr className="bg-amber-50">
                            <td colSpan="4" className="p-2 text-right text-amber-800 font-medium">Sundry Items:</td>
                            <td colSpan={
                              (showBomDistributorPrice ? 2 : 0) + 
                              (showBomInstallerPrice ? 2 : 0) + 
                              (showBomEndUserPrice ? 2 : 0)
                            } className="p-2 text-right text-amber-800 font-bold">
                              £{calculateSundryTotal(sundryItems).toFixed(2)}
                            </td>
                          </tr>
                          <tr className="bg-green-100">
                            <td colSpan="4" className="p-2 text-right text-green-900 font-bold">Grand Total (incl. Sundry):</td>
                            {showBomDistributorPrice && (
                              <td colSpan="2" className="p-2 text-right text-purple-900 font-bold">
                                £{((result.trade_subtotal || 0) + calculateSundryTotal(sundryItems)).toFixed(2)}
                              </td>
                            )}
                            {showBomInstallerPrice && (
                              <td colSpan="2" className="p-2 text-right text-green-900 font-bold">
                                £{((result.installer_subtotal || 0) + calculateSundryTotal(sundryItems)).toFixed(2)}
                              </td>
                            )}
                            {showBomEndUserPrice && (
                              <td colSpan="2" className="p-2 text-right text-blue-900 font-bold">
                                £{((result.enduser_subtotal || 0) + calculateSundryTotal(sundryItems)).toFixed(2)}
                              </td>
                            )}
                          </tr>
                        </>
                      )}
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Project Support Dialog */}
      <ProjectSupportDialog
        open={showProjectSupport}
        onOpenChange={(open) => {
          setShowProjectSupport(open);
          if (!open) {
            setSupportDeclined(true);
          }
        }}
        quoteData={{
          project_name: projectName,
          combined_bom: result?.bom,
          installer_price: result?.final_installer_price,
          installer_total: result?.final_installer_price,
          total: result?.final_total
        }}
        configuratorType="Bio CVSecurity"
      />

      {/* Sundry Item Dialog */}
      <Dialog open={showSundryDialog} onOpenChange={setShowSundryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSundryIndex !== null ? 'Edit Sundry Item' : 'Add Sundry Item'}</DialogTitle>
            <DialogDescription>
              Add custom line items such as labour, cabling, commissioning, travel, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sundry-sku">SKU (optional)</Label>
              <Input
                id="sundry-sku"
                placeholder="e.g. LABOUR-001"
                value={sundryForm.sku}
                onChange={(e) => setSundryForm({...sundryForm, sku: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sundry-description">Description *</Label>
              <Input
                id="sundry-description"
                placeholder="e.g. Installation labour, Cabling, Travel"
                value={sundryForm.description}
                onChange={(e) => setSundryForm({...sundryForm, description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sundry-quantity">Quantity *</Label>
                <Input
                  id="sundry-quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="1"
                  value={sundryForm.quantity}
                  onChange={(e) => setSundryForm({...sundryForm, quantity: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sundry-price">Unit Price (£) *</Label>
                <Input
                  id="sundry-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={sundryForm.unit_price}
                  onChange={(e) => setSundryForm({...sundryForm, unit_price: e.target.value})}
                />
              </div>
            </div>
            {sundryForm.quantity && sundryForm.unit_price && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Line Total:</span>
                  <span className="font-semibold">
                    £{((parseFloat(sundryForm.quantity) || 0) * (parseFloat(sundryForm.unit_price) || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSundryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSundryItem}>
              {editingSundryIndex !== null ? 'Update' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time & Attendance - Contact ZKTeco Dialog */}
      <Dialog open={showTaContactDialog} onOpenChange={setShowTaContactDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Contact ZKTeco Representative
            </DialogTitle>
            <DialogDescription>
              For more than 3,000 users, please contact your ZKTeco representative for a custom quote.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              The standard Time & Attendance licenses support up to 3,000 users. For larger deployments, 
              our team can provide a tailored solution to meet your requirements.
            </p>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Contact:</strong> sales@zkteco.co.uk
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setTaUserCount("3000");
              setShowTaContactDialog(false);
            }}>
              Use 3,000 Users (Max)
            </Button>
            <Button onClick={() => setShowTaContactDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BioCVSecurity;
